// apps/client/src/ui/screens/villagerTalk.tsx (M2-T11) — a conversation with a villager (spec §9.13), in M1-T19's dialogue panel with
// the villager's name in the cream tag. Each tap shows the next line; after the last come the choices: Give a gift, Bye! and, for the
// mayor, the stay/move toggle. The goodbye line is held back until Bye; a tap after it ends the talk. Closing the screen any other way
// (a villager leaving, another screen taking over) ends the talk too, except when it hands over to the gift picker, which comes back.
import type { StringKey } from '@whiskerton/content';
import type { DialogueLine, VillagerId } from '@whiskerton/shared';
import { useEffect, useRef, useState } from 'react';
import type { ActionAnim } from '../../characters/avatar';
import { villagerEmote } from '../../game/systems/villagers';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import type { ScreenDef } from '../router';
import { DialoguePanel } from './dialogue';

declare module '../router' { interface ScreenParams { villagerTalk: { villager: VillagerId } } }

/** Set while the talk hands over to the gift screen, so the talk's unmount does not end the conversation (the gift screen returns). */
export const handoff = { gift: false };
const EMOTE: Partial<Record<DialogueLine['mood'], ActionAnim>> = { happy: 'heart', surprised: 'surprised', sad: 'sleepy' };

function VillagerTalk({ villager, close }: { villager: VillagerId; close(): void }) {
  const t = useT();
  const talk = useGame((s) => (s.villagersTalk?.villager === villager ? s.villagersTalk : null));
  const v = useGame((s) => s.villagersList.find((x) => x.id === villager) ?? null);
  const mayor = useGame((s) => !!s.you && !!s.town && s.town.mayor === s.you.resident);
  const [bye, setBye] = useState(false);
  const closing = useRef(false);
  const shut = () => { if (!closing.current) { closing.current = true; close(); } };

  // Ends the talk when the screen goes away without Bye or the gift handoff (the router dropped it, or the town was left).
  useEffect(() => () => {
    if (handoff.gift) { handoff.gift = false; return; }
    const s = useGame.getState();
    if (s.screens.some((x) => x.id === 'villagerTalk')) return;   // still on the stack (remounted)
    if (s.villagersTalk?.villager === villager) void s.villagersEndTalk();
  }, [villager]);
  // The conversation ended elsewhere (the villager moved away, the talk was ended): close.
  useEffect(() => { if (!talk) shut(); });

  const line = talk && !bye ? talk.lines[Math.min(talk.index, talk.lines.length - 1)] ?? null : null;
  const lineKey = talk ? `${talk.convo}|${talk.index}|${talk.lines.map((l) => l.id ?? l.text).join(',')}` : '';
  useEffect(() => {
    const anim = line && talk && talk.index < talk.lines.length ? EMOTE[line.mood] : undefined;
    if (anim) villagerEmote(villager, anim);
  }, [lineKey]);   // once per shown line

  if (!talk) return null;
  const name = v?.name ?? '';
  const end = () => { closing.current = true; void useGame.getState().villagersEndTalk(); close(); };
  if (bye) {
    const g = talk.goodbye;
    return <DialoguePanel speaker={name} speakerKind="villager" text={g?.text ?? ''} mood={g?.mood ?? 'neutral'} onNext={end} />;
  }
  if (talk.index < talk.lines.length) {
    const next = () => useGame.setState({ villagersTalk: { ...talk, index: talk.index + 1 } });
    return <DialoguePanel speaker={name} speakerKind="villager" text={line?.text ?? ''} mood={line?.mood ?? 'neutral'} onNext={next} />;
  }
  const gift = () => { handoff.gift = true; closing.current = true; close(); useGame.getState().openScreen('gift', { villager }); };
  const sayBye = () => { if (talk.goodbye) setBye(true); else end(); };
  const protect = () => { if (v) void useGame.getState().villagersProtect(v.id, !v.protected); };
  const protectKey: StringKey = v?.protected ? 'villagers.talkUnprotect' : 'villagers.talkProtect';
  return (
    <DialoguePanel speaker={name} speakerKind="villager" text={line?.text ?? ''} mood={line?.mood ?? 'neutral'} onNext={() => {}}>
      <button type="button" className="btn btn-quiet" data-testid="talk-gift" onClick={gift}>{t('villagers.talkGift')}</button>
      {mayor && v && <button type="button" className="btn btn-quiet" data-testid="talk-protect" onClick={protect}>{t(protectKey, { name })}</button>}
      <button type="button" className="btn btn-primary" data-testid="talk-bye" onClick={sayBye}>{t('villagers.talkBye')}</button>
    </DialoguePanel>
  );
}
export const screen: ScreenDef<'villagerTalk'> = {
  id: 'villagerTalk', layer: 'overlay', component: ({ params, close }) => <VillagerTalk villager={params.villager} close={close} />,
};
